import { ManoObraGlobalENTITY, MaterialMOGlobalDTO, SubManoObraDTO } from "sigo-entities"
import { DtoAsignacionValorizacion, DtoFormAddValorizacion } from "../Domain/DtoAsignacionValorizacion"
import { Children, useEffect, useState } from "react"
import Form from 'react-bootstrap/Form'
import { Button, Col, FloatingLabel, Row, Table } from "react-bootstrap"
import * as formik from 'formik'
import * as yup from 'yup'
import { AdapterGenerico } from "../../../../shared/Infraestructure/AdapterGenerico"

interface IProps {
    dataManoObraGlobal: ManoObraGlobalENTITY[]
    asignacion: DtoAsignacionValorizacion
    formAddValorizacion: {
        values: DtoFormAddValorizacion
        touched: formik.FormikTouched<DtoFormAddValorizacion>
        errors: formik.FormikErrors<DtoFormAddValorizacion>
        handleBlur: (e: React.FocusEvent<any>) => void
        setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void
    }
}

export function SubMO(props: IProps) {
    const { asignacion, dataManoObraGlobal, formAddValorizacion } = props

    const { Formik } = formik
    const [manoObraGlobal, setManoObraGlobal] = useState<ManoObraGlobalENTITY>()
    const schema = yup.object({
        cantidad: yup
            .number()
            .required('La cantidad es obligatoria')
            .integer('La cantidad debe ser un número entero')
            .moreThan(0, 'La cantidad debe ser mayor a 0'),
        subMO: yup
            .string()
            .trim()
            .min(1, 'Debes seleccionar una SubMO')
            .required('Debes seleccionar una SubMO')
    })
    type IValues = yup.InferType<typeof schema>
    const initialValues: IValues = {
        cantidad: 0,
        subMO: ''
    }

    useEffect(() => {
        const manoObraGlobal = dataManoObraGlobal.find(e => e.Codigo === asignacion.ManoObra.Codigo)
        setManoObraGlobal(manoObraGlobal)
    }, [asignacion])

    const handleSubmit = (values: IValues, { resetForm }: formik.FormikHelpers<IValues>) => {
        try {
            if (!manoObraGlobal) {
                throw new Error(`No se pudo obtener mano obra global.`)
            }
            const subMOAggregates = formAddValorizacion.values.SubMO
            const exist = subMOAggregates.find(e => e.MaterialMOGlobal.Codigo === values.subMO)
            if (exist) {
                throw new Error(`Ya se agregó sub mano obra ${values.subMO}.`)
            }
            const SubMO = manoObraGlobal.Material.find(e => e.Codigo === values.subMO)
            if (!SubMO) {
                throw new Error(`No se pudo obtener sub mano obra.`)
            }

            const newItem = new SubManoObraDTO()
            newItem.Cantidad = values.cantidad
            newItem.CantidadSMO = SubMO.Cantidad
            newItem.Codigo = SubMO.Codigo
            newItem.Nombre = SubMO.Nombre

            const newData = [...subMOAggregates, { SubManoObra: newItem, MaterialMOGlobal: SubMO }]

            const CantidadMOPreliquidada = newData.reduce((ac, el) => ac += el.SubManoObra.Cantidad * el.SubManoObra.CantidadSMO, 0)

            formAddValorizacion.setFieldValue('SubMO', newData)
            formAddValorizacion.setFieldValue('CantidadMOPreliquidada', CantidadMOPreliquidada)
            formAddValorizacion.setFieldValue('CantidadUOPreliquidada', 0)
            resetForm()
        } catch (error) {
            console.error(error)
            AdapterGenerico.createMessage('Alerta', (error as Error).message, 'warning', false)
        }
    }

    const handleClickDelete = (e: MaterialMOGlobalDTO) => {
        try {
            const newData = formAddValorizacion.values.SubMO.filter(el => el.MaterialMOGlobal.Codigo !== e.Codigo)
            const CantidadMOPreliquidada = newData.reduce((ac, el) => ac += el.SubManoObra.Cantidad * el.SubManoObra.CantidadSMO, 0)
            formAddValorizacion.setFieldValue('SubMO', newData)
            formAddValorizacion.setFieldValue('CantidadMOPreliquidada', CantidadMOPreliquidada)
            formAddValorizacion.setFieldValue('CantidadUOPreliquidada', 0)
        } catch (error) {
            console.error(error)
            AdapterGenerico.createMessage('Alerta', (error as Error).message, 'warning', false)
        }
    }

    return (
        <Formik<IValues>
            validationSchema={schema}
            onSubmit={handleSubmit}
            initialValues={initialValues}
        >
            {({ handleSubmit, handleChange, values, touched, errors }) => {
                return (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Row className='mb-3'>
                            <Form.Group as={Col} controlId='validationFormik01'>
                                <FloatingLabel controlId='floatingSelect' label='Sub Mano Obra'>
                                    <Form.Select
                                        name='subMO'
                                        value={values.subMO}
                                        onChange={handleChange}
                                        isValid={touched.subMO && !errors.subMO}
                                        isInvalid={!!errors.subMO}
                                    >
                                        <option value=''>Abrir este menú de selección</option>
                                        {
                                            manoObraGlobal
                                                ? manoObraGlobal.Material
                                                    .filter(e => !formAddValorizacion.values.SubMO.some(el => el.MaterialMOGlobal.Codigo === e.Codigo))
                                                    .map(
                                                        (e, i) => <option key={i} value={e.Codigo}>{e.Nombre}</option>
                                                    )
                                                : <option disabled>No se encontró mano de obra</option>
                                        }
                                    </Form.Select>
                                    <Form.Control.Feedback>¡Se ve bien!</Form.Control.Feedback>
                                    <Form.Control.Feedback type='invalid'>{errors.subMO}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>
                        <Row className='mb-3'>
                            <Form.Group as={Col} controlId='validationFormik01'>
                                <FloatingLabel controlId='floatingSelect' label='Cantidad'>
                                    <Form.Control
                                        type='number'
                                        name='cantidad'
                                        value={values.cantidad}
                                        onChange={handleChange}
                                        isValid={touched.cantidad && !errors.cantidad}
                                        isInvalid={!!errors.cantidad}
                                    />
                                    <Form.Control.Feedback>¡Se ve bien!</Form.Control.Feedback>
                                    <Form.Control.Feedback type='invalid'>{errors.cantidad}</Form.Control.Feedback>
                                </FloatingLabel>
                            </Form.Group>
                        </Row>
                        <Row className='mb-3'>
                            <Col>
                                <Button
                                    variant='outline-success'
                                    type='submit'
                                    className='w-100'
                                >
                                    AGREGAR
                                </Button>
                            </Col>
                        </Row>
                        {
                            !!formAddValorizacion.values.SubMO.length && (
                                <Row className='mb-3'>
                                    <Table responsive>
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th key='nombre'>Nombre</th>
                                                <th key='cantidad'>Cantidad</th>
                                                <th key='factor'>Factor</th>
                                                <th key='opc'></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                Children.toArray(
                                                    formAddValorizacion.values.SubMO.map((e, i) => {
                                                        return (
                                                            <tr>
                                                                <td>{i + 1}</td>
                                                                <td key={e.SubManoObra.Nombre}>{e.SubManoObra.Nombre}</td>
                                                                <td key={e.SubManoObra.Cantidad}>{e.SubManoObra.Cantidad}</td>
                                                                <td key={e.SubManoObra.CantidadSMO}>{e.SubManoObra.CantidadSMO}</td>
                                                                <td key='opc'>
                                                                    <Button
                                                                        variant='danger'
                                                                        size='sm'
                                                                        onClick={() => handleClickDelete(e.MaterialMOGlobal)}
                                                                    >
                                                                        <i className='fa-solid fa-trash' />
                                                                    </Button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Row>
                            )
                        }
                    </Form>
                )
            }}
        </Formik>
    )
}